import { FaceNoresponse } from '@icims-design-system/icons';
import Question from '@mui/icons-material/HelpOutlineOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, DialogContentText, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAlertUpdater } from '../../context/AlertContext';
import { useCustomerId, useSpaData, useSubmittalId } from '../../hooks';
import { useCancelFeedbackMutation } from '../../services/dashboardQueries';
import { useResendFeedbackMutation } from '../../services/feedbackQueries';
import Modal from '../../util/Modal';
import CardInfo from './CardInfo';
import MoreActionsMenu from './MoreActionsMenu';
import RatingAndRecommendation from './RatingAndRecommendation';
import TooltipWrapper from './TooltipWrapper';

const classes = {
	cardBody: {
		backgroundColor: 'background.paper',
		display: 'flex',
		padding: '20px',
		borderRadius: '5px',
		justifyContent: 'space-between',
		flexDirection: 'column',
	},
	cardInfo: {
		display: 'flex',
		margin: '5px 0',
	},
	cardInfoText: {
		marginLeft: '5px',
	},
	icon: {
		color: 'grey',
	},
	questionIcon: {
		color: 'grey',
		marginRight: '5px',
	},
	tooltip: {
		fontSize: '1rem',
	},
};

const FEEDBACK_REQUEST_CANCELING = 'toasts.feedback_request_canceling';
const FEEDBACK_REQUEST_CANCELED = 'toasts.feedback_request_canceled';
const FEEDBACK_REQUEST_CANCEL_ERROR = 'toasts.feedback_request_cancel_error';
const FEEDBACK_REQUEST_RESENDING = 'toasts.feedback_request_resending';
const FEEDBACK_REQUEST_RESENT = 'toasts.feedback_request_resent';
const FEEDBACK_REQUEST_RESEND_ERROR = 'toasts.feedback_request_resend_error';
const FILTER_DAYS = 60;

const getValueByCondition = (cond, val1, val2) => (cond ? val1 : val2);

// eslint-disable-next-line complexity
function FeedbackCard({ feedbackInfo, view, handleOnClick, hideFeedback }) {
	const { t } = useTranslation(['candidate-feedback', 'common']);
	const [anchorEl, setAnchorEl] = useState(null);

	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
	const [isResendRequestDialogOpen, setIsResendRequestDialogOpen] = useState(false);
	const [actionText, setActionText] = useState(FEEDBACK_REQUEST_CANCELING);

	const { alert } = useAlertUpdater();

	const customerId = useCustomerId();
	const submittalId = useSubmittalId();
	const { loggedInUser: interviewerId = '' } = useSpaData();
	const { mutate: cancelFeedbackRequest, isPending: cancelingFeedbackRequest } =
		useCancelFeedbackMutation({ interviewerId, customerId, filterDays: FILTER_DAYS }, submittalId);
	const { mutate: resendFeedbackRequest, isPending: resendingFeedbackRequest } =
		useResendFeedbackMutation(feedbackInfo.feedbackRequestId);

	const handleIsCancelDialogOpen = (val) => {
		setIsCancelDialogOpen(val);
	};

	const handleIsResendRequestDialogOpen = (val) => {
		setIsResendRequestDialogOpen(val);
	};

	// Show an alert when the feedback request is being canceled
	useEffect(() => {
		if (cancelingFeedbackRequest) {
			alert({ message: actionText });
		}
	}, [actionText, alert, cancelingFeedbackRequest]);

	// Show an alert when the feedback request is being resent
	useEffect(() => {
		if (resendingFeedbackRequest) {
			alert({ message: actionText });
		}
	}, [actionText, alert, resendingFeedbackRequest]);

	const { name } = feedbackInfo.candidate;
	const { isUserAllowedToCancelFeedbackRequests, loggedInUser } = useSpaData();
	const {
		actionTextStart,
		actionTextResume,
		actionTextView,
		actionTextCancelled,
		statusTextNotStarted,
		statusTextInProgress,
		statusTextRecentlyCompleted,
		statusTextCancelled,
		candidate,
		interviewer,
		status,
		feedbackButtonDisabledHelpText,
		startButtonLabel,
		resumeButtonLabel,
		viewButtonLabel,
		cancelledButtonLabel,
	} = useMemo(
		() => ({
			actionTextStart: t('start'),
			actionTextResume: t('continue_or_resume_feedback'),
			actionTextView: t('view'),
			actionTextCancelled: t('common:canceled'),
			statusTextNotStarted: t('feedback_state.not_started'),
			statusTextInProgress: t('feedback_state.in_progress'),
			statusTextRecentlyCompleted: t('completed'),
			statusTextCancelled: t('common:canceled'),
			candidate: t('candidate'),
			interviewer: t('interviewer'),
			status: t('status'),
			feedbackButtonDisabledHelpText: t('feedback_button_disabled_help_text'),
			startButtonLabel: t('start_feedback', {
				candidateName: name,
				feedbackFormName:
					feedbackInfo.feedbackRequestForm.displayNamesMap !== null
						? Object.values(feedbackInfo.feedbackRequestForm.displayNamesMap)[0]
						: '',
			}),
			resumeButtonLabel: t('continue_feedback', {
				candidateName: name,
				feedbackFormName:
					feedbackInfo.feedbackRequestForm.displayNamesMap !== null
						? Object.values(feedbackInfo.feedbackRequestForm.displayNamesMap)[0]
						: '',
			}),
			viewButtonLabel: t('view_feedback', {
				candidateName: name,
				interviewerName: feedbackInfo.assignedTo.name,
				feedbackFormName:
					feedbackInfo.feedbackRequestForm.displayNamesMap !== null
						? Object.values(feedbackInfo.feedbackRequestForm.displayNamesMap)[0]
						: '',
			}),
			cancelledButtonLabel: t('button_titles.canceled', { name }),
		}),
		[t, name, feedbackInfo.feedbackRequestForm.displayNamesMap, feedbackInfo.assignedTo.name],
	);
	const generateDescriptionId = (feedbackRequestId) => `feedback-description-${feedbackRequestId}`;

	const statusMaps = {
		NOT_STARTED: {
			color: '#f9a84c',
			actionText: actionTextStart,
			statusText: statusTextNotStarted,
			ariaLabel: getValueByCondition(
				feedbackInfo.disabled,
				feedbackButtonDisabledHelpText,
				startButtonLabel,
			),
		},
		IN_PROGRESS: {
			color: '#1b5bd8',
			actionText: actionTextResume,
			statusText: statusTextInProgress,
			ariaLabel: getValueByCondition(
				feedbackInfo.disabled,
				feedbackButtonDisabledHelpText,
				resumeButtonLabel,
			),
		},
		SUBMITTED: {
			color: '#48a8af',
			actionText: actionTextView,
			statusText: statusTextRecentlyCompleted,
			ariaLabel: getValueByCondition(hideFeedback && feedbackInfo.disabled, null, viewButtonLabel),
			ariaDescribedBy: getValueByCondition(
				hideFeedback && feedbackInfo.disabled,
				generateDescriptionId(feedbackInfo.feedbackRequestId),
				null,
			),
			descriptionText: getValueByCondition(
				hideFeedback && feedbackInfo.disabled,
				t('hide_feedback_until_submitted', {
					interviewerName: feedbackInfo.assignedTo.name,
					candidateName: feedbackInfo.candidate.name,
					formName:
						feedbackInfo.feedbackRequestForm.displayNamesMap !== null
							? Object.values(feedbackInfo.feedbackRequestForm.displayNamesMap)[0]
							: 'feedback form',
				}),
				null,
			),
		},
		CANCELLED: {
			color: '#b1b2b3',
			actionText: actionTextCancelled,
			statusText: statusTextCancelled,
			ariaLabel: cancelledButtonLabel,
		},
	};

	const getAriaDescribedBy = ({ feedbackState }) =>
		statusMaps[feedbackState]?.ariaDescribedBy || null;

	const getDescriptionText = ({ feedbackState }) =>
		statusMaps[feedbackState]?.descriptionText || null;

	const getFeedbackStatusColor = ({ feedbackState, feedbackRequestStatus }) => {
		if (feedbackRequestStatus === 'CANCELLED') {
			return statusMaps.CANCELLED?.color || 'grey';
		}
		return statusMaps[feedbackState]?.color || 'grey';
	};

	const getActionButtonText = ({ feedbackState, feedbackRequestStatus }) => {
		if (feedbackRequestStatus === 'CANCELLED') {
			return statusMaps.CANCELLED?.actionText || '';
		}

		if (view === 'feedbackTabView' && feedbackInfo.disabled) {
			return statusMaps.SUBMITTED?.actionText || '';
		}
		return statusMaps[feedbackState]?.actionText || '';
	};

	const getStatusText = ({ feedbackState, feedbackRequestStatus }) => {
		if (feedbackRequestStatus === 'CANCELLED') {
			return statusMaps.CANCELLED?.statusText || '';
		}

		return statusMaps[feedbackState]?.statusText || '';
	};

	const getAriaLabel = ({ feedbackState, feedbackRequestStatus }) => {
		if (feedbackRequestStatus === 'CANCELLED') {
			return statusMaps.CANCELLED?.ariaLabel || '';
		}
		return statusMaps[feedbackState]?.ariaLabel || '';
	};

	const renderHelpIcon = () => {
		if (
			view === 'feedbackTabView' &&
			feedbackInfo.disabled &&
			feedbackInfo.feedbackRequestStatus !== 'CANCELLED'
		) {
			return (
				<Tooltip title={feedbackButtonDisabledHelpText}>
					<span role="img" aria-label={feedbackButtonDisabledHelpText} tabIndex={0}>
						<Question sx={classes.questionIcon} />
					</span>
				</Tooltip>
			);
		}
		return null;
	};

	const showMoreActions = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleCancelFeedbackRequestClick = () => {
		setActionText(t(FEEDBACK_REQUEST_CANCELING));
		cancelFeedbackRequest(
			{ feedbackRequestId: feedbackInfo.feedbackRequestId },
			{
				onSuccess: () => {
					alert({ message: t(FEEDBACK_REQUEST_CANCELED) });
				},
				onError: () => {
					alert({
						message: t(FEEDBACK_REQUEST_CANCEL_ERROR),
					});
				},
			},
		);
		handleClose();
	};

	const handleResendRequestClick = () => {
		setActionText(t(FEEDBACK_REQUEST_RESENDING));
		resendFeedbackRequest(
			{ feedbackRequestId: feedbackInfo.feedbackRequestId },
			{
				onSuccess: () => {
					alert({ message: t(FEEDBACK_REQUEST_RESENT) });
				},
				onError: () => {
					alert({
						message: t(FEEDBACK_REQUEST_RESEND_ERROR),
					});
				},
			},
		);
		handleClose();
	};
	/**
	 * Users can cancel feedback requests if they are given permission to cancel all feedback requests
	 * or if the logged-in user is the requester (to cancel a feedback request created by themselves)
	 **/
	const canUserCancelFeedback =
		isUserAllowedToCancelFeedbackRequests ||
		(loggedInUser ? parseInt(loggedInUser, 10) === feedbackInfo.requesterId : false);

	/**
	 * Feedback requests are actionable if the feedback request status is not already CANCELLED
	 * and the feedback state is IN_PROGRESS or NOT_STARTED
	 **/
	const isFeedbackActionable =
		feedbackInfo.feedbackRequestStatus !== 'CANCELLED' &&
		(feedbackInfo.feedbackState === 'IN_PROGRESS' || feedbackInfo.feedbackState === 'NOT_STARTED');

	/**
	 * Show a tooltip informing the user that they cannot cancel the feedback request
	 * if they do not have permission to cancel feedback requests and if the feedback request is cancelable
	 **/
	const showCancelToolTip = !canUserCancelFeedback && isFeedbackActionable;

	return (
		<Box
			sx={{
				...classes.cardBody,
				margin: view === 'feedbackTabView' ? '0px 0' : '16px 0',
			}}
			borderLeft={`4px solid ${getFeedbackStatusColor(feedbackInfo)}`}
			data-testid="feedback"
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
				<Box marginRight={1}>
					<Typography variant="body2" color="text.secondary">
						{view === 'dashboardView' ? candidate : interviewer}
					</Typography>
					<Typography variant="h2" sx={{ wordBreak: 'break-word' }}>
						{view === 'dashboardView' ? feedbackInfo.candidate.name : feedbackInfo.assignedTo.name}
					</Typography>
				</Box>
				<Box display="flex" alignItems="center">
					{!hideFeedback ? renderHelpIcon() : null}
					<TooltipWrapper hideFeedback={hideFeedback}>
						<Button
							variant="outlined"
							onClick={handleOnClick}
							disabled={feedbackInfo.disabled}
							data-testid="status-button"
							aria-label={getAriaLabel(feedbackInfo)}
							aria-describedby={getAriaDescribedBy(feedbackInfo)}
						>
							{getActionButtonText(feedbackInfo)}
						</Button>
					</TooltipWrapper>

					{/* Hidden description element for screen readers when hideFeedback is true */}
					{getDescriptionText(feedbackInfo) && (
						<div
							id={generateDescriptionId(feedbackInfo.feedbackRequestId)}
							className="screenreader"
						>
							{getDescriptionText(feedbackInfo)}
						</div>
					)}
					{
						<>
							<Tooltip title={t('more actions')} classes={{ tooltip: classes.tooltip }}>
								<IconButton
									data-testid={`moreActions-iconButton`}
									id={`moreActions-iconButton`}
									aria-label={t('more actions')}
									aria-haspopup="true"
									sx={{ marginLeft: '6px' }}
									onClick={(event) => showMoreActions(event)}
								>
									<MoreVertIcon />
								</IconButton>
							</Tooltip>
							<MoreActionsMenu
								anchorEl={anchorEl}
								view={view}
								open={Boolean(anchorEl)}
								onClose={handleClose}
								isFeedbackActionable={isFeedbackActionable}
								showCancelToolTip={showCancelToolTip}
								onCancelClick={() => setIsCancelDialogOpen(true)}
								onResendClick={() => setIsResendRequestDialogOpen(true)}
								feedbackRequestId={feedbackInfo.feedbackRequestId}
								t={t}
								classes={classes}
							/>
						</>
					}
				</Box>
			</Box>
			<RatingAndRecommendation feedbackInfo={feedbackInfo} view={view} />
			{view === 'feedbackTabView' ? (
				<Box sx={classes.cardInfo}>
					<FaceNoresponse sx={classes.icon} />
					<Typography
						sx={classes.cardInfoText}
					>{`${status}: ${getStatusText(feedbackInfo)}`}</Typography>
				</Box>
			) : null}
			<CardInfo feedbackInfo={feedbackInfo} view={view} classes={classes} />
			<Modal
				open={isCancelDialogOpen}
				handleOpen={handleIsCancelDialogOpen}
				dialogTitle={t('cancel_feedback')}
				dialogContent={
					<DialogContentText>
						{t('are_you_sure_you want_to_cancel_this_feedback_request')}
					</DialogContentText>
				}
				actionButtonText={t('common:yes')}
				onActionButtonClick={() => {
					handleCancelFeedbackRequestClick();
					handleIsCancelDialogOpen(false);
				}}
				cancelButtonText={t('common:no')}
				onCancelButtonClick={() => {
					handleIsCancelDialogOpen(false);
				}}
				labelId="cancel-feedback-request-confirmation-modal"
			/>
			<Modal
				open={isResendRequestDialogOpen}
				handleOpen={handleIsResendRequestDialogOpen}
				dialogTitle={t('resend_request')}
				dialogContent={
					<DialogContentText>
						{t('are_you_sure_you_want_to_resend_this_feedback_request')}
					</DialogContentText>
				}
				actionButtonText={t('common:yes')}
				onActionButtonClick={() => {
					handleResendRequestClick();
					handleIsResendRequestDialogOpen(false);
				}}
				cancelButtonText={t('common:no')}
				onCancelButtonClick={() => {
					handleIsResendRequestDialogOpen(false);
				}}
				labelId="resend-feedback-request-confirmation-modal"
			/>
		</Box>
	);
}

export default FeedbackCard;
